import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { User, MapPin, ClipboardList, Eye, FileText, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const { user, token, updateProfile, isInternalBuyer } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({ name: '', phone: '', billing_address: '', shipping_address: '' });
  const [billingInfo, setBillingInfo] = useState({
    razon_social: '',
    rfc: '',
    regimen_fiscal: '',
    uso_cfdi: '',
    email_facturacion: '',
    calle: '',
    numero_exterior: '',
    numero_interior: '',
    colonia: '',
    codigo_postal: '',
    ciudad: '',
    estado: '',
    pais: 'México'
  });
  const [orders, setOrders] = useState([]);
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingBilling, setLoadingBilling] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    setProfile({ name: user.name || '', phone: user.phone || '', billing_address: user.billing_address || '', shipping_address: user.shipping_address || '' });
    axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setOrders(r.data.orders || [])).catch(() => {});
    axios.get(`${API}/returns/my-returns`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setReturns(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    
    // Cargar datos de facturación
    axios.get(`${API}/billing/info`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (r.data.billing_info) {
          setBillingInfo(r.data.billing_info);
        }
      })
      .catch(() => {});
  }, [user, token, navigate]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateProfile(profile);
      toast.success('Perfil actualizado');
    } catch (err) {
      toast.error('Error al actualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBilling = async () => {
    setLoadingBilling(true);
    try {
      await axios.put(`${API}/billing/info`, billingInfo, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      toast.success('Información de facturación actualizada');
    } catch (err) {
      toast.error('Error al actualizar información de facturación');
    } finally {
      setLoadingBilling(false);
    }
  };

  if (!user) return null;

  const statusColors = { pendiente: 'outline', pagado: 'secondary', cancelado: 'destructive', en_proceso: 'outline', enviado: 'secondary', entregado: 'secondary', compra_interna: 'outline' };
  const statusLabels = { pendiente: 'Pendiente', pagado: 'Pagado', cancelado: 'Cancelado', en_proceso: 'En Proceso', enviado: 'Enviado', entregado: 'Entregado', compra_interna: 'Compra Interna' };
  const shippingMeta = {
    pending:         { label: 'Pendiente',     className: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
    label_generated: { label: 'Guía Generada', className: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
    in_transit:      { label: 'En Tránsito',   className: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
    delivered:       { label: 'Entregado',     className: 'bg-green-500/15 text-green-300 border-green-500/30' },
    failed:          { label: 'Fallido',       className: 'bg-red-500/15 text-red-300 border-red-500/30' },
  };

  return (
    <div className="py-8 md:py-12">
      <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-display font-semibold tracking-tight">Mi Perfil</h1>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-sm text-muted-foreground">{user.email}</p>
              {isInternalBuyer && <Badge className="bg-amber-100 text-amber-300 border-amber-200">Comprador Interno</Badge>}
              {user.role === 'admin' && <Badge>Administrador</Badge>}
            </div>
          </div>
        </div>

        <Tabs defaultValue="profile">
          <TabsList>
            <TabsTrigger value="profile" className="gap-2"><User className="h-4 w-4" /> Datos</TabsTrigger>
            <TabsTrigger value="addresses" className="gap-2"><MapPin className="h-4 w-4" /> Direcciones</TabsTrigger>
            <TabsTrigger value="billing" className="gap-2"><FileText className="h-4 w-4" /> Facturación</TabsTrigger>
            <TabsTrigger value="orders" className="gap-2"><ClipboardList className="h-4 w-4" /> Pedidos</TabsTrigger>
            <TabsTrigger value="returns" className="gap-2" data-testid="profile-returns-tab"><RotateCcw className="h-4 w-4" /> Devoluciones</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-6">
            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre</Label>
                    <Input value={profile.name} onChange={e => setProfile({...profile, name: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <Input value={profile.phone} onChange={e => setProfile({...profile, phone: e.target.value})} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Correo</Label>
                  <Input value={user.email} disabled />
                </div>
                <Button onClick={handleSave} disabled={loading}>{loading ? 'Guardando...' : 'Guardar Cambios'}</Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="addresses" className="mt-6">
            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="space-y-2">
                  <Label>Dirección de Facturación</Label>
                  <Input value={profile.billing_address} onChange={e => setProfile({...profile, billing_address: e.target.value})} placeholder="Dirección completa" />
                </div>
                <div className="space-y-2">
                  <Label>Dirección de Envío</Label>
                  <Input value={profile.shipping_address} onChange={e => setProfile({...profile, shipping_address: e.target.value})} placeholder="Dirección completa" />
                </div>
                <Button onClick={handleSave} disabled={loading}>{loading ? 'Guardando...' : 'Guardar Direcciones'}</Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="billing" className="mt-6">
            <Card>
              <CardContent className="p-6 space-y-6">
                <div>
                  <h3 className="font-display font-semibold text-lg mb-4">Datos Fiscales</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Razón Social *</Label>
                      <Input 
                        value={billingInfo.razon_social} 
                        onChange={e => setBillingInfo({...billingInfo, razon_social: e.target.value})}
                        placeholder="Nombre o Razón Social"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>RFC *</Label>
                      <Input 
                        value={billingInfo.rfc} 
                        onChange={e => setBillingInfo({...billingInfo, rfc: e.target.value.toUpperCase()})}
                        placeholder="RFC (13 caracteres)"
                        maxLength={13}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Régimen Fiscal</Label>
                      <Select value={billingInfo.regimen_fiscal} onValueChange={v => setBillingInfo({...billingInfo, regimen_fiscal: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccione régimen" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="601">601 - General de Ley Personas Morales</SelectItem>
                          <SelectItem value="603">603 - Personas Morales con Fines no Lucrativos</SelectItem>
                          <SelectItem value="605">605 - Sueldos y Salarios e Ingresos Asimilados a Salarios</SelectItem>
                          <SelectItem value="606">606 - Arrendamiento</SelectItem>
                          <SelectItem value="607">607 - Régimen de Enajenación o Adquisición de Bienes</SelectItem>
                          <SelectItem value="608">608 - Demás ingresos</SelectItem>
                          <SelectItem value="610">610 - Residentes en el Extranjero sin Establecimiento Permanente en México</SelectItem>
                          <SelectItem value="611">611 - Ingresos por Dividendos</SelectItem>
                          <SelectItem value="612">612 - Personas Físicas con Actividades Empresariales y Profesionales</SelectItem>
                          <SelectItem value="614">614 - Ingresos por intereses</SelectItem>
                          <SelectItem value="615">615 - Régimen de los ingresos por obtención de premios</SelectItem>
                          <SelectItem value="616">616 - Sin obligaciones fiscales</SelectItem>
                          <SelectItem value="620">620 - Sociedades Cooperativas de Producción que optan por diferir sus ingresos</SelectItem>
                          <SelectItem value="621">621 - Incorporación Fiscal</SelectItem>
                          <SelectItem value="622">622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras</SelectItem>
                          <SelectItem value="623">623 - Opcional para Grupos de Sociedades</SelectItem>
                          <SelectItem value="624">624 - Coordinados</SelectItem>
                          <SelectItem value="625">625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas</SelectItem>
                          <SelectItem value="626">626 - Régimen Simplificado de Confianza</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Uso de CFDI</Label>
                      <Select value={billingInfo.uso_cfdi} onValueChange={v => setBillingInfo({...billingInfo, uso_cfdi: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccione uso" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="G01">G01 - Adquisición de mercancías</SelectItem>
                          <SelectItem value="G02">G02 - Devoluciones, descuentos o bonificaciones</SelectItem>
                          <SelectItem value="G03">G03 - Gastos en general</SelectItem>
                          <SelectItem value="I01">I01 - Construcciones</SelectItem>
                          <SelectItem value="I02">I02 - Mobiliario y equipo de oficina por inversiones</SelectItem>
                          <SelectItem value="I03">I03 - Equipo de transporte</SelectItem>
                          <SelectItem value="I04">I04 - Equipo de computo y accesorios</SelectItem>
                          <SelectItem value="I05">I05 - Dados, troqueles, moldes, matrices y herramental</SelectItem>
                          <SelectItem value="I06">I06 - Comunicaciones telefónicas</SelectItem>
                          <SelectItem value="I07">I07 - Comunicaciones satelitales</SelectItem>
                          <SelectItem value="I08">I08 - Otra maquinaria y equipo</SelectItem>
                          <SelectItem value="D01">D01 - Honorarios médicos, dentales y gastos hospitalarios</SelectItem>
                          <SelectItem value="D02">D02 - Gastos médicos por incapacidad o discapacidad</SelectItem>
                          <SelectItem value="D03">D03 - Gastos funerales</SelectItem>
                          <SelectItem value="D04">D04 - Donativos</SelectItem>
                          <SelectItem value="D05">D05 - Intereses reales efectivamente pagados por créditos hipotecarios (casa habitación)</SelectItem>
                          <SelectItem value="D06">D06 - Aportaciones voluntarias al SAR</SelectItem>
                          <SelectItem value="D07">D07 - Primas por seguros de gastos médicos</SelectItem>
                          <SelectItem value="D08">D08 - Gastos de transportación escolar obligatoria</SelectItem>
                          <SelectItem value="D09">D09 - Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones</SelectItem>
                          <SelectItem value="D10">D10 - Pagos por servicios educativos (colegiaturas)</SelectItem>
                          <SelectItem value="S01">S01 - Sin efectos fiscales</SelectItem>
                          <SelectItem value="CP01">CP01 - Pagos</SelectItem>
                          <SelectItem value="CN01">CN01 - Nómina</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Email de Facturación</Label>
                      <Input 
                        type="email"
                        value={billingInfo.email_facturacion} 
                        onChange={e => setBillingInfo({...billingInfo, email_facturacion: e.target.value})}
                        placeholder="correo@ejemplo.com"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-display font-semibold text-lg mb-4">Dirección Fiscal</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Calle</Label>
                      <Input 
                        value={billingInfo.calle} 
                        onChange={e => setBillingInfo({...billingInfo, calle: e.target.value})}
                        placeholder="Nombre de la calle"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-2">
                        <Label>No. Exterior</Label>
                        <Input 
                          value={billingInfo.numero_exterior} 
                          onChange={e => setBillingInfo({...billingInfo, numero_exterior: e.target.value})}
                          placeholder="123"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>No. Interior</Label>
                        <Input 
                          value={billingInfo.numero_interior} 
                          onChange={e => setBillingInfo({...billingInfo, numero_interior: e.target.value})}
                          placeholder="A (opcional)"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Colonia</Label>
                      <Input 
                        value={billingInfo.colonia} 
                        onChange={e => setBillingInfo({...billingInfo, colonia: e.target.value})}
                        placeholder="Colonia"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Código Postal</Label>
                      <Input 
                        value={billingInfo.codigo_postal} 
                        onChange={e => setBillingInfo({...billingInfo, codigo_postal: e.target.value})}
                        placeholder="00000"
                        maxLength={5}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Ciudad</Label>
                      <Input 
                        value={billingInfo.ciudad} 
                        onChange={e => setBillingInfo({...billingInfo, ciudad: e.target.value})}
                        placeholder="Ciudad"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Estado</Label>
                      <Input 
                        value={billingInfo.estado} 
                        onChange={e => setBillingInfo({...billingInfo, estado: e.target.value})}
                        placeholder="Estado"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>País</Label>
                      <Input 
                        value={billingInfo.pais} 
                        onChange={e => setBillingInfo({...billingInfo, pais: e.target.value})}
                        placeholder="México"
                      />
                    </div>
                  </div>
                </div>

                <Button onClick={handleSaveBilling} disabled={loadingBilling}>
                  {loadingBilling ? 'Guardando...' : 'Guardar Información de Facturación'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            {orders.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardList className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">No tienes pedidos aún</p>
                <Link to="/tienda"><Button className="mt-4">Ir a la Tienda</Button></Link>
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map(order => (
                  <Card key={order.id}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-display font-semibold text-sm">{order.order_number}</p>
                          {order.envia_tracking_number && shippingMeta[order.shipping_status] && (
                            <Badge variant="outline" className={`text-[10px] ${shippingMeta[order.shipping_status].className}`} data-testid={`order-shipping-badge-${order.order_number}`}>
                              {shippingMeta[order.shipping_status].label}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleDateString('es-MX')}</p>
                        <p className="text-xs text-muted-foreground">{order.items?.length} producto(s)</p>
                      </div>
                      <div className="text-right flex items-center gap-3">
                        <div>
                          <Badge variant={statusColors[order.order_status] || 'outline'}>{statusLabels[order.order_status] || order.order_status}</Badge>
                          <p className="text-sm font-display font-semibold mt-1" data-testid="profile-order-total-mxn">${(order.total_mxn ?? order.total ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</p>
                        </div>
                        <Link to={`/pedido/${order.id}`}><Button variant="ghost" size="sm"><Eye className="h-4 w-4" /></Button></Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="returns" className="mt-6" data-testid="profile-returns-content">
            {returns.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <RotateCcw className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Aún no has solicitado devoluciones</p>
                  <p className="text-xs text-muted-foreground mt-1">Puedes solicitar una devolución desde el detalle de un pedido entregado.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {returns.map(ret => (
                  <Card key={ret.id} data-testid={`return-card-${ret.order_number}`}>
                    <CardContent className="p-4 flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-display font-semibold text-sm">{ret.order_number}</p>
                          {(() => {
                            const meta = {
                              requested: { label: 'Solicitada', className: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
                              approved:  { label: 'Aprobada',   className: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
                              rejected:  { label: 'Rechazada',  className: 'bg-red-500/15 text-red-300 border-red-500/30' },
                              completed: { label: 'Completada', className: 'bg-green-500/15 text-green-300 border-green-500/30' },
                            }[ret.status];
                            return meta ? (
                              <Badge variant="outline" className={`text-[10px] ${meta.className}`} data-testid={`return-status-badge-${ret.order_number}`}>
                                {meta.label}
                              </Badge>
                            ) : null;
                          })()}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {({
                            producto_danado: 'Producto dañado',
                            producto_incorrecto: 'Producto incorrecto',
                            no_cumple_expectativas: 'No cumple expectativas',
                            otro: 'Otro',
                          })[ret.reason] || ret.reason}
                          {' • '}
                          {new Date(ret.created_at).toLocaleDateString('es-MX')}
                        </p>
                        {ret.comments && <p className="text-xs text-muted-foreground mt-1 truncate max-w-md">"{ret.comments}"</p>}
                      </div>
                      <Link to={`/pedido/${ret.order_id}`}><Button variant="ghost" size="sm"><Eye className="h-4 w-4" /></Button></Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
